import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Loader2, AlertTriangle, ArrowLeft, Phone, ShieldCheck } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PHONE = "+420 775 659 129";

export default function SharedOrderPage() {
  const { token } = useParams();
  const nav = useNavigate();
  const [shared, setShared] = useState(null);
  const [loadErr, setLoadErr] = useState("");
  const [form, setForm] = useState({
    name: "", phone: "", email: "",
    vehicle: "", spz_vin: "", preferred_term: "", note: "",
    gdpr_consent: false,
  });
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => { document.title = "Objednávka přes odkaz · STK Komfort"; }, []);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/shared-orders/${token}`)
      .then(({ data }) => setShared(data))
      .catch((ex) => setLoadErr(ex?.response?.data?.detail || "Odkaz není platný."));
  }, [token]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    if (!form.name.trim() || !form.phone.trim()) { setErrorMsg("Vyplňte jméno a telefon."); setStatus("error"); return; }
    if (!form.gdpr_consent) { setErrorMsg("Potvrďte prosím souhlas se zpracováním osobních údajů."); setStatus("error"); return; }
    setStatus("loading");
    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || undefined,
        vehicle: form.vehicle.trim() || undefined,
        spz_vin: form.spz_vin.trim() || undefined,
        preferred_term: form.preferred_term.trim() || undefined,
        note: form.note.trim() || undefined,
        service_id: shared.service_id,
        service_name: shared.service_name,
        service_category: shared.service_category,
        price_label: shared.price_label,
        amount: shared.amount,
        gdpr_consent: true,
      };
      const { data } = await axios.post(`${API}/shared-orders/${token}/complete`, payload);
      // Redirect to standard order success: use /objednavka with state — easiest: render inline confirmation
      nav("/klient", { replace: false });
      void data;
    } catch (ex) {
      setErrorMsg(ex?.response?.data?.detail || "Nepodařilo se vytvořit objednávku.");
      setStatus("error");
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0D17] text-slate-200">
      <header className="border-b border-white/10 bg-[#0A0D17]/85 backdrop-blur-xl">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <span className="block h-11 w-11 rounded-xl bg-[#0A0D17] ring-1 ring-amber-300/20"
              style={{ backgroundImage: "url(/brand/logo.png)", backgroundRepeat: "no-repeat", backgroundSize: "150% auto", backgroundPosition: "50% 18%" }} />
            <div className="leading-tight">
              <div className="font-heading text-base font-bold text-white">STK Komfort</div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-amber-300/80">Znojmo</div>
            </div>
          </Link>
          <a href={`tel:${PHONE.replace(/\s+/g, "")}`} className="hidden sm:inline-flex items-center gap-2 rounded-lg bg-gradient-to-b from-amber-300 to-amber-500 px-4 py-2.5 text-sm font-bold text-slate-900">
            <Phone className="h-4 w-4" /> {PHONE}
          </a>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 lg:px-8 py-12 lg:py-16">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-amber-300/80 hover:text-amber-300">
          <ArrowLeft className="h-4 w-4" /> Zpět na úvod
        </Link>

        {loadErr ? (
          <div className="mt-10 rounded-2xl border border-rose-400/30 bg-rose-400/10 p-6 text-rose-200" data-testid="shared-error">
            <AlertTriangle className="h-5 w-5 inline mr-2" /> {loadErr}
            <div className="mt-4 text-sm">
              <Link to="/objednavka" className="text-amber-300 hover:underline">Vytvořit objednávku ručně →</Link>
            </div>
          </div>
        ) : !shared ? (
          <div className="mt-20 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline mr-2" />Ověřuji odkaz…</div>
        ) : (
          <>
            <div className="mt-6 rounded-3xl border border-amber-300/30 bg-amber-300/5 p-6">
              <div className="inline-flex items-center gap-2 rounded-full border border-amber-300/30 bg-amber-300/10 px-3 py-1 text-[10px] uppercase tracking-[0.22em] text-amber-300/90 font-semibold">
                <ShieldCheck className="h-3 w-3" /> Domluvená nabídka
              </div>
              <h1 className="font-heading mt-5 text-3xl font-extrabold text-white tracking-tight">
                {shared.service_name}
              </h1>
              <div className="mt-2 text-sm text-slate-400">{shared.service_category}</div>
              <div className="mt-4 font-heading text-4xl font-extrabold text-amber-200">{shared.amount.toFixed(2)} Kč</div>
              {shared.note && (
                <div className="mt-4 rounded-xl border border-white/10 bg-white/[0.03] p-3 text-sm text-slate-300">
                  <strong className="text-white">Poznámka od týmu: </strong>{shared.note}
                </div>
              )}
              <p className="mt-4 text-xs text-slate-500">
                Tato cena je pro vás předem domluvená. Po vyplnění údajů obdržíte QR platbu s variabilním symbolem.
              </p>
            </div>

            <form onSubmit={submit} className="mt-8 space-y-5" data-testid="shared-order-form">
              <Section title="Kontaktní údaje *">
                <Grid>
                  <Field label="Jméno a příjmení *" name="name" value={form.name} onChange={handleChange} required testid="shared-name" />
                  <Field label="Telefon *" name="phone" value={form.phone} onChange={handleChange} type="tel" required testid="shared-phone" />
                  <Field label="E-mail" name="email" value={form.email} onChange={handleChange} type="email" className="sm:col-span-2" testid="shared-email" />
                </Grid>
              </Section>

              <Section title="Vozidlo">
                <Grid>
                  <Field label="Vozidlo" name="vehicle" value={form.vehicle} onChange={handleChange} placeholder="Škoda Octavia" testid="shared-vehicle" />
                  <Field label="SPZ / VIN" name="spz_vin" value={form.spz_vin} onChange={handleChange} testid="shared-spzvin" />
                  <Field label="Preferovaný termín" name="preferred_term" value={form.preferred_term} onChange={handleChange} className="sm:col-span-2" testid="shared-term" />
                </Grid>
              </Section>

              <Section title="Poznámka">
                <textarea name="note" value={form.note} onChange={handleChange} rows={3}
                  className="w-full rounded-xl border border-white/15 bg-[#0A0D17] px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-amber-300 focus:ring-2 focus:ring-amber-300/30 outline-none resize-y"
                  placeholder="Nepovinná zpráva pro Lenku…" data-testid="shared-note" />
              </Section>

              <div className="rounded-2xl border border-amber-300/15 bg-white/[0.03] p-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" name="gdpr_consent" checked={form.gdpr_consent} onChange={handleChange} data-testid="shared-gdpr" className="mt-0.5 h-4 w-4 accent-amber-400" />
                  <span className="text-xs text-slate-300 leading-relaxed">
                    Souhlasím se zpracováním osobních údajů podle{" "}
                    <Link to="/ochrana-osobnich-udaju" target="_blank" rel="noopener noreferrer" className="text-amber-300 underline">Zásad OOÚ</Link>{" "}
                    a se zněním <Link to="/obchodni-podminky" target="_blank" rel="noopener noreferrer" className="text-amber-300 underline">Obchodních podmínek</Link>.
                  </span>
                </label>
              </div>

              {status === "error" && errorMsg && (
                <div data-testid="shared-form-error" className="rounded-xl border border-rose-400/30 bg-rose-400/10 p-3 text-xs text-rose-200 flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" /> {errorMsg}
                </div>
              )}

              <button type="submit" disabled={status === "loading"} data-testid="shared-submit"
                className="w-full rounded-xl bg-gradient-to-b from-amber-300 to-amber-500 hover:from-amber-200 hover:to-amber-400 disabled:opacity-50 px-7 py-4 font-bold text-slate-900">
                {status === "loading" ? <><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Vytvářím objednávku…</> : "Potvrdit objednávku a získat QR platbu"}
              </button>
              <p className="text-xs text-slate-500 text-center">
                Po potvrzení budete přesměrováni do klientské zóny s QR platbou.
              </p>
            </form>
          </>
        )}
      </main>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="rounded-3xl border border-white/10 bg-white/[0.02] p-5">
      <div className="text-xs uppercase tracking-[0.18em] text-amber-300/90 font-semibold">{title}</div>
      <div className="mt-4">{children}</div>
    </section>
  );
}
function Grid({ children }) { return <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>; }
function Field({ label, name, value, onChange, placeholder, type = "text", required, className, testid }) {
  return (
    <div className={className || ""}>
      <label className="block text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400 mb-1.5">{label}</label>
      <input type={type} name={name} value={value} onChange={onChange} placeholder={placeholder} required={required} data-testid={testid}
        className="w-full rounded-xl border border-white/15 bg-[#0A0D17] px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-amber-300 focus:ring-2 focus:ring-amber-300/30 outline-none transition" />
    </div>
  );
}
